import React, { Fragment, useEffect, useState, useMemo } from 'react';
import {
  PaginationState,
  useReactTable,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  SortingState,
} from '@tanstack/react-table';
import {
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';
import { HeaderIcon, IconSortDesc, IconSortAsc } from 'components/Table';
import PulseLoader from 'components/Loading/Pulse';
import classNames from 'styles/utils/classNames';

interface CommonTableProps<TData> {
  page?: number;
  columns: ColumnDef<TData, any>[];
  data: TData[];
  loading?: boolean;
  enableSorting?: boolean;
  expandableRowRenderer?: (row: TData) => React.ReactNode;
  pageSize?: number;
  totalRecords?: number;
  onPageChange?: (pageIndex: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  isServerSide?: boolean;
  setPage?: (pageIndex: number) => void;
  columnWidths?: number[];
  isUseColGroup?: boolean;
  onSortingChange?: (sorting: SortingState) => void;
  defaultSorting?: { id: string; desc: boolean }[];
  showSectionHeader?: boolean;
  capitalizeHeaders?: boolean;
  sectionHeader?: React.ComponentType<any> | React.ReactNode;
  useDefaultNumericSort?: boolean;
  numericColumns?: string[];
}

export default function TableGrid<TData>({
  page = 0,
  columns,
  columnWidths,
  setPage,
  data,
  loading = false,
  enableSorting = true,
  expandableRowRenderer,
  pageSize = 25,
  totalRecords = 0,
  onPageChange,
  isServerSide = false,
  isUseColGroup = false,
  onSortingChange,
  defaultSorting = [],
  showSectionHeader = false,
  capitalizeHeaders = false,
  sectionHeader,
  useDefaultNumericSort = true,
  numericColumns = [],
}: CommonTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>(defaultSorting ?? []);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: page,
    pageSize,
  });

  const processedColumns = useMemo(() => {
    if (!useDefaultNumericSort) return columns;

    const isBlank = (val: any) => {
      if (val == null) return true;
      if (typeof val === 'string') {
        const t = val.trim();
        return t === '' || t === '--' || t === '-' || t.toLowerCase() === 'n/a';
      }
      return false;
    };

    const numberLike = (v: any) => {
      if (typeof v === 'number') return true;
      if (typeof v === 'string') {
        const withoutParens = v.trim().replace(/^\((.+)\)$/, '-$1');
        let cleaned = withoutParens.replace(/[, %$]/g, '');

        const suffixMatch = cleaned.match(/^(-?\d*\.?\d+)\s*([BMK])$/i);
        if (suffixMatch) {
          return !Number.isNaN(Number(suffixMatch[1]));
        }

        return cleaned !== '' && !Number.isNaN(Number(cleaned));
      }
      return false;
    };

    const toNumber = (v: any) => {
      if (typeof v === 'number') return v;

      const str = v.toString().trim();
      const withoutParens = str.replace(/^\((.+)\)$/, '-$1');
      let cleaned = withoutParens.replace(/[, %$]/g, '');

      const suffixMatch = cleaned.match(/^(-?\d*\.?\d+)\s*([BMKT])$/i);
      if (suffixMatch) {
        const number = Number(suffixMatch[1]);
        const suffix = suffixMatch[2].toUpperCase();

        switch (suffix) {
          case 'T': return number * 1000000000000;
          case 'B': return number * 1000000000;
          case 'M': return number * 1000000;
          case 'K': return number * 1000;
          default: return number;
        }
      }

      return Number(cleaned);
    };

    return columns.map((column) => {
      let columnId = '';
      if ('accessorKey' in column && typeof column.accessorKey === 'string') {
        columnId = column.accessorKey;
      } else if ('accessorFn' in column && 'id' in column && typeof column.id === 'string') {
        columnId = column.id;
      } else if ('id' in column && typeof column.id === 'string') {
        columnId = column.id;
      }

      const shouldApplyNumericSort =
        !('sortingFn' in column && column.sortingFn) &&
        (numericColumns.length === 0 || numericColumns.includes(columnId));

      if (!shouldApplyNumericSort) return column;

      // preserve original accessor
      const originalAccessor =
        'accessorKey' in column
          ? column.accessorKey
          : 'accessorFn' in column
            ? column.accessorFn
            : undefined;

      return {
        ...column,
        accessorFn: (row: any) => {
          let value: any;
          if (typeof originalAccessor === 'string') {
            value = row[originalAccessor];
          } else if (typeof originalAccessor === 'function') {
            value = originalAccessor(row, 0);
          } else {
            value = row[columnId];
          }

          if (isBlank(value)) return undefined;
          if (numberLike(value)) return toNumber(value);
          return value;
        },
        sortingFn: column.sortingFn ?? 'basic',
        sortUndefined: 'last',
      } as typeof column;
    });
  }, [columns, useDefaultNumericSort, numericColumns]);

  const table = useReactTable({
    data,
    columns: processedColumns,
    state: { sorting, pagination },
    sortingFns: {
      nullsLast: (rowA, rowB, columnId) => {
        const aValue = rowA.getValue(columnId);
        const bValue = rowB.getValue(columnId);

        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;

        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      },
    },
    onSortingChange: (updater) => {
      if (onSortingChange) {
        const nextSort = typeof updater === 'function' ? updater(sorting) : updater;
        onSortingChange(nextSort);
      }
      setSorting(updater);
    },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    getPaginationRowModel: !isServerSide ? getPaginationRowModel() : undefined,
    manualPagination: isServerSide,
    pageCount: isServerSide ? Math.ceil(totalRecords / pageSize) : undefined,
    debugTable: false,
    initialState: {
      pagination: {
        pageIndex: page,
        pageSize,
      },
      sorting: defaultSorting ?? [],
    },
  });

  const paginationState = table.getState().pagination;

  const handlePageChange = (pageIndex: number) => {
    const totalPages = table.getPageCount();
    const newPageIndex = Math.max(0, Math.min(pageIndex, totalPages - 1));

    if (isServerSide && onPageChange) {
      onPageChange(newPageIndex);
    }

    if (setPage) {
      setPage(newPageIndex);
    }
    table.setPageIndex(newPageIndex);
  };

  const formatHeaderText = (headerText: string) => {
    if (!capitalizeHeaders) return headerText;
    return headerText.toUpperCase();
  };

  const renderSectionHeader = () => {
    if (!showSectionHeader) return null;

    const paginationInfo = {
      currentPage: paginationState.pageIndex + 1,
      pageSize: paginationState.pageSize,
      totalRecords: isServerSide ? totalRecords : data.length,
      startRecord: paginationState.pageIndex * paginationState.pageSize + 1,
      endRecord: Math.min(
        (paginationState.pageIndex + 1) * paginationState.pageSize,
        isServerSide ? totalRecords : data.length,
      ),
    };

    if (React.isValidElement(sectionHeader)) {
      return React.cloneElement(sectionHeader as React.ReactElement, { paginationInfo });
    }
    if (typeof sectionHeader === 'function') {
      const SectionHeaderComponent = sectionHeader as React.ComponentType<{ paginationInfo?: any }>;
      return <SectionHeaderComponent paginationInfo={paginationInfo} />;
    }

    return sectionHeader;
  };

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: page,
    }));
  }, [page]);

  return (
    <div className="flex flex-col rounded">
      <div className="flex-1 overflow-auto rounded-t">
        <table className={classNames('w-full', isUseColGroup && 'table-fixed')}>
          {isUseColGroup && columns.length > 0 && (
            <colgroup>
              {columns.map((_, idx) => (
                <col
                  key={idx}
                  style={{ width: `${columnWidths?.[idx] || 100 / columns.length}%` }}
                />
              ))}
            </colgroup>
          )}
          <thead className="sticky top-0 z-10 rounded-t bg-palette-black-4">
            {showSectionHeader && (
              <tr>
                <th colSpan={columns.length}>
                  <div className="p-4 pb-0 font-normal">{renderSectionHeader()}</div>
                </th>
              </tr>
            )}
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className='px-3 py-2 text-left text-[.5rem] font-semibold text-label-text transition-all duration-200 ease-in-out'
                    scope="col"
                  >
                    <div
                      className={
                        header.column.getCanSort()
                          ? 'flex cursor-pointer select-none items-center'
                          : 'flex items-center'
                      }
                      onClick={header.column.getToggleSortingHandler()}

                    >
                      <span>
                        {typeof header.column.columnDef.header === 'string'
                          ? formatHeaderText(header.column.columnDef.header)
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </span>
                      {header.column.getCanSort() && (
                        <button
                          onClick={() => header.column.getToggleSortingHandler()}
                          className="ml-2"
                        >
                          {header.column.getIsSorted() === 'desc' ? (
                            <HeaderIcon>
                              <IconSortDesc />
                            </HeaderIcon>
                          ) : header.column.getIsSorted() === 'asc' ? (
                            <HeaderIcon>
                              <IconSortAsc />
                            </HeaderIcon>
                          ) : null}
                        </button>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody className="bg-palette-black-2">
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="py-10 text-center">
                  <PulseLoader />
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <Fragment key={row.id}>
                  <tr className="border-b border-palette-border">
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-3 py-2 text-left text-[.65rem]" data-name={
                        typeof cell.column.columnDef.header === 'string'
                          ? formatHeaderText(cell.column.columnDef.header)
                          : ''
                      }>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                  {expandableRowRenderer && (
                    <tr>
                      <td colSpan={columns.length} className="p-3">
                        {expandableRowRenderer(row.original)}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="sticky bottom-0 z-10 rounded-b bg-palette-black-4 p-4 shadow">
        <div className="flex items-center justify-between">
          <span className="text-sm">
            Showing {paginationState.pageIndex * paginationState.pageSize + 1} to &nbsp;
            {Math.min(
              (paginationState.pageIndex + 1) * paginationState.pageSize,
              isServerSide ? totalRecords : data.length,
            )}
            &nbsp; of {isServerSide ? totalRecords : data.length} results
          </span>
          <nav aria-label="Pagination" className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(paginationState.pageIndex - 1)}
              disabled={!table.getCanPreviousPage()}
              className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 focus:z-20 focus:outline-offset-0"
            >
              <FaChevronLeft aria-hidden="true" className="text-s h-3 w-3" />
            </button>
            {Array.from({ length: table.getPageCount() }, (_, index) => {
              const isCurrentPage = paginationState.pageIndex === index;
              const totalPageCount = table.getPageCount();
              const currentPage = paginationState.pageIndex;
              if (
                index === 0 ||
                index === totalPageCount - 1 ||
                (index >= currentPage - 1 && index <= currentPage + 1)
              ) {
                return (
                  <button
                    key={index}
                    onClick={() => handlePageChange(index)}
                    className={`relative inline-flex items-center rounded px-3.5 py-2 text-xs font-semibold ${isCurrentPage
                      ? 'z-10 bg-brand-primary text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                      : 'focus:z-20 focus:outline-offset-0'
                      }`}
                    aria-current={isCurrentPage ? 'page' : undefined}
                  >
                    {index + 1}
                  </button>
                );
              }

              if (
                (index === currentPage - 2 && index > 1) ||
                (index === currentPage + 2 && index < totalPageCount - 2)
              ) {
                return (
                  <span key={index} className="px-3 py-1 text-gray-500">
                    ...
                  </span>
                );
              }

              return null;
            })}
            <button
              onClick={() => handlePageChange(paginationState.pageIndex + 1)}
              disabled={!table.getCanNextPage()}
              className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 focus:z-20 focus:outline-offset-0"
            >
              <FaChevronRight aria-hidden="true" className="h-3 w-3 text-sm" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
}
